import { useMemo, useState, useCallback, useEffect } from 'react';
import type { AdminModProfile, OtherTokenActivity } from '../api/bipApi';

const DASHBOARD_MC_1M = 1_000_000;
const GMGN_BASE = 'https://gmgn.ai/sol/token/';
const INITIAL_VISIBLE = 10;
const ROWS_PER_PAGE = 10;

type FollowersSortDir = 'asc' | 'desc';

interface AdminModSectionProps {
  text: string;
  table: AdminModProfile[];
  animationDelay?: string;
}

/** Current admin = first admin when sorted by role (admin first) then followers desc. */
function getCurrentAdmin(table: AdminModProfile[]): AdminModProfile | undefined {
  const sorted = [...table].sort((a, b) => {
    const roleOrder = (r: AdminModProfile['role']) => (r === 'admin' ? 0 : 1);
    const byRole = roleOrder(a.role) - roleOrder(b.role);
    if (byRole !== 0) return byRole;
    return (b.followersCount ?? 0) - (a.followersCount ?? 0);
  });
  return sorted.find((p) => p.role === 'admin');
}

function computeDashboard(table: AdminModProfile[]) {
  const currentAdmin = getCurrentAdmin(table);
  const adminFollowers = currentAdmin?.followersCount ?? 0;

  let adminCount = 0;
  let modCount = 0;
  let usersWith1MPlus = 0;

  for (const profile of table) {
    if (profile.role === 'admin') {
      adminCount += 1;
    } else {
      modCount += 1;
    }
    const activities = profile.otherTokenActivities ?? [];
    const has1M = activities.some((a) => (a.maxMc ?? 0) >= DASHBOARD_MC_1M);
    if (has1M) usersWith1MPlus += 1;
  }

  const ctoCount = Math.max(0, adminCount - 1);

  return { adminFollowers, modCount, ctoCount, usersWith1MPlus };
}

function formatMc(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
  return `$${Math.round(value)}`;
}

function truncateAddress(addr: string, head = 4, tail = 4): string {
  if (!addr || addr.length <= head + tail) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

function roleShortLabel(role: string): string {
  return role?.toLowerCase() === 'admin' ? 'Admin' : 'Mod';
}

/** 복사 버튼: 클릭 시 아이콘이 2초간 초록색으로 */
function CopyButton({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [address]);
  return (
    <button
      type="button"
      className={`am-activity-btn ${copied ? 'am-activity-btn--copied' : ''}`}
      onClick={handleCopy}
      title="Copy address"
      aria-label="Copy address"
    >
      <svg className="am-activity-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
      </svg>
    </button>
  );
}

/** MC 내림차순 정렬, 1M 이상 골드 강조. 200x160 카드 가로 나열, 셀 내 횡스크롤 */
function OtherActivitiesCell({ activities }: { activities: OtherTokenActivity[] }) {
  const sorted = useMemo(() => {
    return [...(activities ?? [])].sort((a, b) => (b.maxMc ?? 0) - (a.maxMc ?? 0));
  }, [activities]);

  if (!sorted.length) {
    return <span className="am-cell-empty">—</span>;
  }

  return (
    <div className="am-activities-cell">
      <div className="am-activities-cards">
        {sorted.map((activity, index) => {
          const address = activity.tokenAddress ?? '';
          const symbol = activity.tokenSymbol ?? '—';
          const role = activity.role;
          const maxMc = activity.maxMc ?? 0;
          const is1MPlus = maxMc >= DASHBOARD_MC_1M;
          const gmgnUrl = address ? `${GMGN_BASE}${address}` : '';

          return (
            <div
              key={`${activity.tokenAddress ?? index}-${index}`}
              className={`am-activity-card ${is1MPlus ? 'am-activity-card--1m' : ''}`}
            >
              <div className="am-activity-card-inner">
                <div className="am-activity-card-head">
                  <span className="am-activity-card-symbol" title={symbol}>
                    {symbol}
                  </span>
                  
                  {address && (
                  <>
                    <div className="am-activity-card-address-wrap">
                      <code className="am-activity-card-address" title={address}>
                        <span>{truncateAddress(address)}</span>
                        <div className="am-activity-card-actions">
                          <CopyButton address={address} />
                          {gmgnUrl && (
                            <a
                              href={gmgnUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="am-activity-btn am-activity-btn--link"
                              title="Open GMGN chart"
                              aria-label="Open GMGN chart"
                            >
                              <img src="/static/gmgn.svg" alt="GMGN" className="am-activity-gmgn" />
                            </a>
                          )}
                        </div>
                      </code>

                    </div> 
                  </>
                )}
                </div>
                
                <div className="" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.35rem' }}>
                {role && (
                    <span className={`am-activity-role am-activity-role--${role.toLowerCase()}`}>
                      {roleShortLabel(role)}
                    </span>
                  )}
                   {maxMc > 0 && (
                      <div className="am-activity-card-mc" title="Max market cap">
                        {formatMc(maxMc)}
                      </div>
                    )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SortHeader({
  label,
  currentDir,
  onToggle,
  isActive,
}: {
  label: string;
  currentDir: FollowersSortDir;
  onToggle: () => void;
  isActive: boolean;
}) {
  return (
    <button
      type="button"
      className={`am-col-followers am-col-sortable ${isActive ? 'am-col-sortable--active' : ''}`}
      onClick={onToggle}
      aria-label={isActive ? `Sort by ${label} ${currentDir === 'asc' ? 'ascending' : 'descending'}, click to reverse` : `Sort by ${label}`}
    >
      <span>{label}</span>
      {isActive && (
        <span className="am-sort-arrow" aria-hidden>
          {currentDir === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );
}

export default function AdminModSection({ text, table, animationDelay = '0.4s' }: AdminModSectionProps) {
  const dashboard = useMemo(() => computeDashboard(table), [table]);
  const [followersDir, setFollowersDir] = useState<FollowersSortDir>('desc');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [table.length]);

  const handleFollowersSort = useCallback(() => {
    setFollowersDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  }, []);

  const sortedRows = useMemo(() => {
    return [...table].sort((a, b) => {
      const roleOrder = (r: AdminModProfile['role']) => (r === 'admin' ? 0 : 1);
      const byRole = roleOrder(a.role) - roleOrder(b.role);
      if (byRole !== 0) return byRole;
      const aF = a.followersCount ?? 0;
      const bF = b.followersCount ?? 0;
      return followersDir === 'desc' ? bF - aF : aF - bF;
    });
  }, [table, followersDir]);

  const visibleRows = useMemo(
    () => sortedRows.slice(0, visibleCount),
    [sortedRows, visibleCount],
  );
  const hasMore = sortedRows.length > visibleCount;

  return (
    <section className="result-section fade-in" id="section-admin-mod" style={{ animationDelay }}>
      <div className="section-title-block">
        <h2 className="section-title">Admin & Moderators</h2>
      </div>
      <p className="section-description">{text}</p>

      <div className="smart-money-dashboard admin-mod-dashboard">
        <div className="smart-money-dashboard-item">
          <span className="smart-money-dashboard-value">{dashboard.adminFollowers.toLocaleString()}</span>
          <span className="smart-money-dashboard-label">Admin followers</span>
        </div>
        <div className="smart-money-dashboard-item">
          <span className="smart-money-dashboard-value">{dashboard.modCount}</span>
          <span className="smart-money-dashboard-label">Moderators</span>
        </div>
        <div className="smart-money-dashboard-item">
          <span className="smart-money-dashboard-value">{dashboard.ctoCount}</span>
          <span className="smart-money-dashboard-label">CTO count</span>
        </div>
        <div className="smart-money-dashboard-item">
          <span className="smart-money-dashboard-value">{dashboard.usersWith1MPlus}</span>
          <span className="smart-money-dashboard-label">Admins/Mods<br />with ≥$1M token history</span>
        </div>
      </div>

      <div className="admin-mod-table">
        <div className="admin-mod-header">
          <div className="am-col-name">X User name</div>
          <div className="am-col-handle">X Handle</div>
          <div className="am-col-role">Role</div>
          <SortHeader
            label="Followers"
            currentDir={followersDir}
            onToggle={handleFollowersSort}
            isActive={true}
          />
          <div className="am-col-activities">Other Activities</div>
        </div>
        <div className="admin-mod-body">
          {visibleRows.map((row, index) => {
            const isLoadMoreRow = index >= INITIAL_VISIBLE;
            const delay = isLoadMoreRow
              ? 0.05 + (index - INITIAL_VISIBLE) * 0.02
              : parseFloat(animationDelay) + 0.1 + index * 0.05;
            return (
              <div
                key={`${row.screenName}-${row.role}`}
                className="admin-mod-row"
                style={{ animationDelay: `${delay}s` }}
              >
                <div className="am-col-name">
                  <span className="am-name">{row.name}</span>
                </div>
                <div className="am-col-handle">
                  <a
                    href={`https://x.com/${row.screenName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="am-handle"
                  >
                    @{row.screenName}
                  </a>
                </div>
                <div className="am-col-role">
                  <span className={`am-role-badge am-role-badge--${row.role}`}>{roleShortLabel(row.role)}</span>
                </div>
                <div className="am-col-followers">
                  <span className="am-followers">{row.followersCount.toLocaleString()}</span>
                </div>
                <div className="am-col-activities">
                  <OtherActivitiesCell activities={row.otherTokenActivities ?? []} />
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <div className="smart-money-load-more-wrap">
            <button
              type="button"
              className="smart-money-load-more"
              onClick={() => setVisibleCount((prev) => Math.min(prev + ROWS_PER_PAGE, sortedRows.length))}
              aria-label="Load more rows"
            >
              +
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
